import { useState } from "react"
import { AlertTriangle, ShieldAlert } from "lucide-react"
import { cn } from "@/lib/utils"

interface SafetyDialogProps {
  title: string
  description: string
  warnings?: string[]
  confirmLabel?: string
  variant?: "default" | "destructive"
  typeToConfirm?: string
  onConfirm: () => void
  onCancel: () => void
  isLoading: boolean
}

export function SafetyDialog({
  title,
  description,
  warnings,
  confirmLabel = "Confirm",
  variant = "default",
  typeToConfirm,
  onConfirm,
  onCancel,
  isLoading,
}: SafetyDialogProps) {
  const [typedValue, setTypedValue] = useState("")
  const isDestructive = variant === "destructive"
  const needsTyping = Boolean(typeToConfirm)
  const typingMatch = !needsTyping || typedValue === typeToConfirm
  const canConfirm = !isLoading && typingMatch

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
        <div className="flex items-start gap-3 mb-3">
          {isDestructive ? (
            <ShieldAlert className="size-5 text-red-600 mt-0.5 shrink-0" />
          ) : (
            <AlertTriangle className="size-5 text-amber-600 mt-0.5 shrink-0" />
          )}
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        </div>

        <p className="text-sm text-gray-700 mb-3">{description}</p>

        {warnings && warnings.length > 0 && (
          <div
            className={cn(
              "rounded-md p-3 mb-4",
              isDestructive
                ? "bg-red-50 border border-red-200"
                : "bg-amber-50 border border-amber-200",
            )}
          >
            <ul
              className={cn(
                "text-xs space-y-1 list-disc list-inside",
                isDestructive ? "text-red-700" : "text-amber-700",
              )}
            >
              {warnings.map((w, i) => (
                <li key={i}>{w}</li>
              ))}
            </ul>
          </div>
        )}

        {needsTyping && (
          <div className="mb-4">
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Type <span className="font-mono font-bold text-red-600">{typeToConfirm}</span> to confirm
            </label>
            <input
              type="text"
              value={typedValue}
              onChange={(e) => setTypedValue(e.target.value)}
              placeholder={typeToConfirm}
              className="w-full px-3 py-2 text-sm border rounded-md font-mono focus:outline-hidden focus:ring-2 focus:ring-red-300 border-red-300"
              autoFocus
            />
          </div>
        )}

        <div className="flex gap-3">
          <button
            onClick={onCancel}
            disabled={isLoading}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={!canConfirm}
            className={cn(
              "flex-1 px-4 py-2 rounded-md text-sm font-medium text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors",
              isDestructive
                ? "bg-red-600 hover:bg-red-700"
                : "bg-amber-600 hover:bg-amber-700",
            )}
          >
            {isLoading ? "Processing..." : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}

// Re-export under old name for backward compat during transition
export { SafetyDialog as ConfirmDialog }
